import { useFormik } from 'formik';
import { Filter, RefreshCw, Search } from 'lucide-react';
import styled from 'styled-components';

import { Button } from '../../../../components/atoms/button/button';
import { Input } from '../../../../components/atoms/input/input';
import { Select } from '../../../../components/atoms/select/select';
import { Permission } from '../../../../types/permission';
import { UsuariosFilters } from '../../../../types/usuario';

interface Props {
  initialValues: UsuariosFilters;
  permissions: Permission[];
  onApply: (values: UsuariosFilters) => void;
  onClear: () => void;
}

const Wrapper = styled.section`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 18px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const Actions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const statusOptions = [
  { label: 'Todos', value: '' },
  { label: 'Ativos', value: 'true' },
  { label: 'Inativos', value: 'false' },
];

export const UsuariosFiltersBar = ({
  initialValues,
  permissions,
  onApply,
  onClear,
}: Props) => {
  const formik = useFormik<UsuariosFilters>({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => onApply(values),
  });

  const permissionOptions = [
    { label: 'Todas as permissões', value: '' },
    ...permissions.map((p) => ({ label: p.key, value: p.key })),
  ];

  return (
    <Wrapper
      as="form"
      onSubmit={formik.handleSubmit}
      aria-label="Filtros de usuários"
    >
      <Field>
        Busca
        <Input
          name="search"
          placeholder="Buscar por nome ou e-mail"
          value={formik.values.search ?? ''}
          onChange={formik.handleChange}
          startIcon={<Search size={16} />}
        />
      </Field>

      <Field>
        Status
        <Select
          options={statusOptions}
          name="active"
          value={
            formik.values.active === undefined || formik.values.active === ''
              ? ''
              : formik.values.active
                ? 'true'
                : 'false'
          }
          onChange={(e) => {
            const v = e.target.value;
            formik.setFieldValue(
              'active',
              v === '' ? '' : v === 'true',
            );
          }}
        />
      </Field>

      <Field>
        Permissão
        <Select
          options={permissionOptions}
          name="permission"
          value={formik.values.permission ?? ''}
          onChange={formik.handleChange}
        />
      </Field>

      <Actions>
        <Button
          variant="ghost"
          type="button"
          startIcon={<RefreshCw size={14} />}
          onClick={() => {
            formik.resetForm({ values: {} });
            onClear();
          }}
        >
          Limpar
        </Button>
        <Button
          variant="primary"
          type="submit"
          startIcon={<Filter size={14} />}
        >
          Aplicar filtros
        </Button>
      </Actions>
    </Wrapper>
  );
};
